'use client'

import { useTranslations } from 'next-intl'
import { CSSProperties } from 'react'

import { useAirbridgeContext } from '@/shared/config'
import { Link, TranslationKey } from '@/shared/i18n'
import { cn } from '@/shared/lib'

import ChatIcon from '../asset/chat.svg'
import ContractIcon from '../asset/contract.svg'
import HomeActiveIcon from '../asset/home-active.svg'
import HomeIcon from '../asset/home.svg'
import MapActiveIcon from '../asset/map-active.svg'
import MapIcon from '../asset/map.svg'
import MyIcon from '../asset/my.svg'

const BOTTOM_NAV_ITEMS: {
  key: TranslationKey<'navigation'>
  href?: string
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  activeIcon?: React.FC<React.SVGProps<SVGSVGElement>>
  ctaParam?: string
}[] = [
  {
    key: 'home',
    href: '/guest/main',
    icon: HomeIcon,
    activeIcon: HomeActiveIcon,
  },
  {
    key: 'map',
    href: '/guest/search',
    icon: MapIcon,
    activeIcon: MapActiveIcon,
  },
  {
    key: 'lease',
    icon: ContractIcon,
    ctaParam: 'menu_leases',
  },
  {
    key: 'chat',
    icon: ChatIcon,
    ctaParam: 'menu_chats',
  },
  {
    key: 'my',
    icon: MyIcon,
    ctaParam: 'menu_my',
  },
]

type Props = {
  pathname: string
  style?: CSSProperties
  className?: string
}

export const BottomNavigationBarUI = ({
  pathname,
  style,
  className,
}: Props) => {
  const t = useTranslations('navigation')
  const airbridge = useAirbridgeContext()

  const handleClick = (ctaParam?: string) => {
    airbridge.openDeeplink({
      ctaParams: {
        cta_param_1: ctaParam ?? '',
        cta_param_2: '',
        cta_param_3: '',
      },
    })
  }

  return (
    <nav
      className={cn(
        'fixed right-0 bottom-0 left-0 z-50 bg-white after:absolute after:top-0 after:left-0 after:h-[0.5px] after:w-full after:bg-gray-200 after:content-[""]',
        className,
      )}
      style={style}
    >
      <ul className="flex items-center">
        {BOTTOM_NAV_ITEMS.map(({ key, href, icon, activeIcon, ctaParam }) => {
          const isSelected = href ? pathname.includes(href) : false
          const IconComponent = isSelected && activeIcon ? activeIcon : icon
          const itemClassName = cn(
            'flex w-full flex-col items-center gap-px pt-[9px] text-gray-400',
            isSelected && 'text-black',
          )

          if (href) {
            return (
              <li key={key} className="flex-1">
                <Link href={href} className={itemClassName}>
                  <IconComponent className="h-6 w-6" />
                  <Label label={t(key)} isSelected={isSelected} />
                </Link>
              </li>
            )
          }

          return (
            <li key={key} className="flex-1">
              <button
                key={key}
                type="button"
                className={itemClassName}
                onClick={() => {
                  handleClick(ctaParam)
                }}
              >
                <IconComponent className="h-6 w-6" />
                <Label label={t(key)} isSelected={isSelected} />
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

const Label = ({
  label,
  isSelected,
}: {
  label: string
  isSelected: boolean
}) => {
  return (
    <span
      className={cn(
        'typo-caption3 leading-[16px] text-gray-400',
        isSelected && 'text-black',
      )}
    >
      {label}
    </span>
  )
}
